import {
  getBaseState,
  getEntityAdapterReducers,
  handleFulfilledAction,
  handleMultipleLoadingActions,
} from '@hwm/ui-lib';
import { IActiveNamedList } from '@models/INamedList';
import { ISite } from '@models/ISite';
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BulkActionsContextType } from '@shared/components/Layout/Siders/SiteBulkActions/SiteBulkActions.utils';
import { EntityReducers } from '../../EntityReducers';
import { SITES_PAGE_SIZE, thunks } from './SitesThunks';

const entityAdapter = createEntityAdapter<ISite>({});

export interface SiteStateListPaginationModel {
  total: number;
  currPage: number;
}

const slice = createSlice({
  name: EntityReducers.sites,
  initialState: {
    ...getBaseState(entityAdapter),
    total: null as number,
    currPage: null as number,
    pageCount: null as number,
    totalSelected: null as number,
    activeNamedList: null as IActiveNamedList,
    bulkContext: 'selected' as (typeof BulkActionsContextType)[number],
    customBulkRefresh: undefined as () => void,
  },
  reducers: {
    ...getEntityAdapterReducers(entityAdapter),
    setNamedList: (state, action) => {
      state.activeNamedList = action.payload;
    },
    setSiteListPagination: (state, action: PayloadAction<SiteStateListPaginationModel>) => {
      state.total = action?.payload?.total ?? state.total;
      state.currPage = action?.payload?.currPage ?? state.currPage;
      state.pageCount = action?.payload?.total
        ? Math.ceil(Math.ceil(action.payload.total / SITES_PAGE_SIZE))
        : state.pageCount;
    },
    setTotalSelected: (state, action: PayloadAction<number>) => {
      state.totalSelected = action.payload;
    },
    setBulkContext: (state, action: PayloadAction<(typeof BulkActionsContextType)[number]>) => {
      state.bulkContext = action.payload;
    },
    setCustomBulkRefresh: (state, action: PayloadAction<() => void>) => {
      state.customBulkRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions(
      [
        thunks.getSitesForMap,
        thunks.getClusters,
        //
        thunks.getSiteDetails,
        thunks.saveSiteDetails,
        thunks.uploadPhotos,
        thunks.getSitesList,
        thunks.saveSiteLocation,
        //
        thunks.loadFavoriteNamedList,
        thunks.addSiteAsSelected,
        thunks.removeSiteAsSelected,
        thunks.getSelectedSitesIds,
        thunks.addAllSitesAsSelected,
        thunks.removeAllSitesAsSelected,
      ],
      builder
    );
    handleFulfilledAction(thunks.getSitesForMap, builder, (state, payload) => {
      payload.sites &&
        entityAdapter.upsertMany(
          state,
          payload.sites.flatMap((item) => item.sites)
        );

      payload.masts &&
        entityAdapter.upsertMany(
          state,
          payload.masts.flatMap((mast) => mast.sites)
        );
    });
    handleFulfilledAction(thunks.getClusters, builder, (state, payload) => {
      const clusters = payload.clusters ?? [];

      const sites = clusters?.map((s) => s.siteDetails as ISite)?.filter(Boolean);

      entityAdapter.upsertMany(state, sites);
    });
    handleFulfilledAction(thunks.saveSiteLocation, builder, (state, payload) => {
      entityAdapter.upsertOne(state, payload);
    });
    handleFulfilledAction(thunks.getSitesList, builder, (state, payload) => {
      if (payload?.items?.length >= 0) {
        entityAdapter.upsertMany(state, payload.items as ISite[]);

        state.currPage = payload.currentPage;
        state.total = payload.itemsTotal;
        state.totalSelected = payload.totalSelectedItems;
        state.pageCount = Math.ceil(payload.itemsTotal / SITES_PAGE_SIZE);
      }
    });
    handleFulfilledAction(thunks.getSiteDetails, builder, (state, payload) => {
      entityAdapter.upsertOne(state, payload);
    });
    handleFulfilledAction(thunks.saveSiteDetails, builder, (state, payload) => {
      entityAdapter.upsertOne(state, payload);
    });
    handleFulfilledAction(thunks.uploadPhotos, builder, () => {});
    //
    handleFulfilledAction(thunks.loadFavoriteNamedList, builder, () => {});
    handleFulfilledAction(thunks.getSelectedSitesIds, builder, () => {});
    handleFulfilledAction(thunks.addSiteAsSelected, builder, (state, payload) => {
      entityAdapter.updateOne(state, {
        id: payload.siteId,
        changes: { isSelected: true },
      });
      const total = state.totalSelected;
      state.totalSelected = Math.max(0, total + 1);
    });
    handleFulfilledAction(thunks.removeSiteAsSelected, builder, (state, payload) => {
      entityAdapter.updateOne(state, {
        id: payload.siteId,
        changes: { isSelected: false },
      });
      const total = state.totalSelected;
      state.totalSelected = Math.max(0, total - 1);
    });
    handleFulfilledAction(thunks.addAllSitesAsSelected, builder, () => {});
    handleFulfilledAction(thunks.removeAllSitesAsSelected, builder, () => {});
  },
});

export const siteReducer = {
  slice,
  entityAdapter: entityAdapter,
  thunks,
};
